// apps/frontend/src/components/reports/buildStatus.ts
//
// One place for the customer-facing wording of a background report build
// (plan 2026-08-23 report-creation-background-and-deterministic-templates).
//
// `build_error_kind` is a CLOSED enum on the backend precisely so the
// customer never reads exception text. That contract only holds if every
// surface — the global toast, the report page, the list — maps the same kind
// to the same sentence, so the mapping lives here and nowhere else.
//
// An unrecognised kind degrades to the generic sentence rather than being
// rendered: a newly added backend kind must never leak a raw identifier into
// the UI.

import type { ReportBuildErrorKind, ReportBuildStatus, ReportDTO } from "@/api/reports";

/** The build is still running (or waiting to run). */
export function isBuildInFlight(
  status: ReportBuildStatus | null | undefined,
): boolean {
  return status === "QUEUED" || status === "RUNNING";
}

/** What the report's written parts are called, for the reader.
 *
 * A report built from BLOCKS has no summary. It has readings: an analysis
 * block per section, plus takeaways and recommendations. Calling those "the
 * summary" was wrong on every block report, and since 2026-09-26 it was wrong
 * twice over, because one failed reading used to discard all of them
 * (`report_build_runner.py`, contained that day). A report written from an
 * instruction does have one summary, and keeps the old words. */
type BuildReport = {
  build_status?: ReportBuildStatus | null;
  build_error_kind?: ReportBuildErrorKind | null;
  /** Built from blocks. Absent reads as false, the narrative shape. */
  composed?: boolean | null;
  /** How much of the last build's writing landed. Absent on a report no
   *  background build has reported on: the copy then stays wordy rather than
   *  inventing a number. */
  narrative_total?: number | null;
  narrative_missing?: number | null;
  /** Readings that WERE written and have a sentence missing. */
  narrative_partial?: number | null;
  /** How many readings the one-pass retry could rewrite. NOT the same as
   *  `narrative_missing`: a report built before the set existed has a count
   *  and nothing to target, and the sentence must not promise a targeted
   *  retry there. */
  narrative_retryable?: number | null;
};

/** READY, but the written parts are not all there — the report is usable.
 *
 * THREE kinds land here and they need different words: `narrative_failed` is
 * the model failing outright, `narrative_partial` is the model placing most of
 * its answer and one update being dropped, and `not_draft` is the review gate
 * refusing to write into a report that is submitted or approved. All three
 * leave the numbers, charts and tables intact.
 *
 * Every surface must ask THIS function rather than compare the kind itself:
 * `ReportBuildNotice` carried its own `=== "narrative_failed"` until
 * 2026-09-29, so adding a kind here would have left the Try-again band dark
 * while this file's tests went green. That is the shape of a test that cannot
 * fail, and a review caught it before it shipped. */
export function isSummaryMissing(report: BuildReport): boolean {
  return (
    report.build_status === "READY" &&
    (report.build_error_kind === "narrative_failed" ||
      report.build_error_kind === "narrative_partial" ||
      report.build_error_kind === "not_draft")
  );
}

export const BUILD_READY_TITLE = "Your report is ready";
export const BUILD_SUMMARY_FAILED_TITLE =
  "Report ready — the summary couldn't be written";
export const BUILD_SUMMARY_FAILED_MESSAGE =
  "The numbers are all there. Open the report and use Edit to add a summary.";
export const BUILD_READINGS_FAILED_TITLE =
  "Report ready — some readings couldn't be written";
export const BUILD_READINGS_FAILED_MESSAGE =
  "The numbers, charts and tables are all there. Use Try again above, or Edit to write the missing readings yourself.";
/** The banner while a build is running. A block report is not writing "the
 *  summary": it is writing one reading per analysis block. Arjen saw the old
 *  sentence on a block report on 2026-09-26, next to a failure message that
 *  also said "summary". */
export function buildInFlightMessage(report: BuildReport): string {
  return report.composed
    ? "Writing the readings… The report below is already live; the written parts appear as they are finished."
    : "Writing the summary… The report below is already live; the summary appears here as soon as it is written.";
}

export const BUILD_READINGS_PARTIAL_TITLE =
  "Report ready — some readings are missing a sentence";
export const BUILD_READINGS_PARTIAL_MESSAGE =
  "The numbers, charts and tables are all there, and so is most of the writing. Use Edit to fill the gaps, or Try again above to rebuild the report.";

export const BUILD_NOT_DRAFT_TITLE =
  "Report ready — the readings were not written";
export const BUILD_NOT_DRAFT_MESSAGE =
  "This report is submitted or approved, so nothing was written into it. Revert it to draft and build it again.";
export const BUILD_FAILED_TITLE = "Report generation failed";

/** The title for a READY build whose narrative step failed. */
export function summaryMissingTitle(report: BuildReport): string {
  if (report.build_error_kind === "not_draft") return BUILD_NOT_DRAFT_TITLE;
  if (report.build_error_kind === "narrative_partial") {
    return BUILD_READINGS_PARTIAL_TITLE;
  }
  return report.composed
    ? BUILD_READINGS_FAILED_TITLE
    : BUILD_SUMMARY_FAILED_TITLE;
}

/** The sentence for a READY build whose narrative step failed.
 *
 * It does NOT claim everything else is up to date. Until 2026-09-26 the
 * report page said exactly that, in a hardcoded copy of this mapping, while
 * every reading in the report was empty. */
export function summaryMissingMessage(report: BuildReport): string {
  if (report.build_error_kind === "not_draft") return BUILD_NOT_DRAFT_MESSAGE;
  if (report.build_error_kind === "narrative_partial") {
    // A DIFFERENT sentence, because a different thing happened: the reading is
    // there and one line of it is not. Telling the customer it "could not be
    // written" would send them to rewrite a block that is almost entirely
    // correct — the mirror of the 2026-09-26 bug, where they were told
    // everything was fine while fourteen blocks were empty.
    const gaps = report.narrative_partial;
    const total = report.narrative_total;
    if (typeof gaps === "number" && typeof total === "number" && gaps > 0 && total > 0) {
      const what = gaps === 1 ? "1 reading" : `${gaps} readings`;
      // The verb agrees. "2 readings of 14 is missing" got written because the
      // singular case was the one anyone read.
      const verb = gaps === 1 ? "is" : "are";
      return (
        `${what} of ${total} ${verb} missing a sentence. ` +
        "The numbers, charts and tables are all there, and so is the rest of the writing. " +
        // "Try again" here rebuilds the whole report, because a gapped reading
        // is deliberately not in the retryable set: re-rolling one has about a
        // 1 in 10 chance of coming back empty. Edit is usually the better
        // answer for a single missing line, so it is named first.
        "Use Edit to fill the gap, or Try again above to rebuild the report."
      );
    }
    return BUILD_READINGS_PARTIAL_MESSAGE;
  }
  if (!report.composed) return BUILD_SUMMARY_FAILED_MESSAGE;
  // "1 of 14" where the build reported it. Thirteen readings present and a
  // sentence saying "some readings" reads as a broken report; the number is
  // what tells the customer whether to bother.
  const missing = report.narrative_missing;
  const total = report.narrative_total;
  if (typeof missing === "number" && typeof total === "number" && missing > 0 && total > 0) {
    const what = missing === 1 ? "1 reading" : `${missing} readings`;
    // NAMES THE BUTTON BESIDE IT, not the menu entry. It said "Use Rebuild
    // report" until 2026-09-30, written when the band's button did a full
    // rebuild — and Arjen read that sentence next to a button labelled Try
    // again that now writes only the failed readings. Two controls, one
    // sentence pointing at the wrong one.
    const how =
      (report.narrative_retryable ?? 0) > 0
        ? "Use Try again above to write just those, or Edit to write them yourself."
        // No recorded set (a report built before targeted retries), so the
        // only thing that helps is the full rebuild the button falls back to.
        : "Use Try again above to rebuild the report, or Edit to write them yourself.";
    // A build can produce BOTH, and the failure used to be the only one
    // mentioned: `narrative_failed` wins the error kind, so a reading written
    // with a gap went unreported next to a reading that failed outright.
    const gaps = report.narrative_partial ?? 0;
    const alsoGaps =
      gaps > 0
        ? ` ${gaps === 1 ? "Another reading is" : `${gaps} other readings are`} ` +
          "missing a sentence."
        : "";
    return (
      `${what} of ${total} could not be written.${alsoGaps} ` +
      "The numbers, charts and tables are all there, and so is the rest of the writing. " +
      how
    );
  }
  return BUILD_READINGS_FAILED_MESSAGE;
}

const GENERIC_FAILURE = "Report generation failed. Please try again.";

/** Fixed customer copy for a FAILED build. */
export function buildFailureMessage(
  kind: ReportBuildErrorKind | null | undefined,
): string {
  switch (kind) {
    case "author_no_longer_member":
      return "Report generation stopped — the author is no longer a member.";
    case "enqueue_failed":
    case "enqueue_lost":
    case "orphaned":
    case "retry_exhausted":
    case "lock_held":
    case "build_failed":
    default:
      return GENERIC_FAILURE;
  }
}

/** Short label for the reports list. */
export function buildBadgeLabel(report: ReportDTO): string | null {
  if (isBuildInFlight(report.build_status)) return "Generating";
  if (isSummaryMissing(report)) {
    if (report.build_error_kind === "not_draft") return "Readings missing";
    if (report.build_error_kind === "narrative_partial") return "Readings incomplete";
    return report.composed ? "Readings failed" : "Summary failed";
  }
  return null;
}
